import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { AppSelectOption } from '../../../../../../../_base-shared/contracts/common.interface';
import { CaseCreditorListFilter } from '../../../../../../../_base-shared/models/Case/CaseCreditorListFilter';
import { DepartmentCategory } from '../../../../../../../_base-shared/models/Department/DepartmentCategory';
import { Creditor } from '../../../../../../../_base-shared/models/Entity/Creditor';
import { Product } from '../../../../../../../_base-shared/models/Product';
import { Status } from '../../../../../../../_base-shared/models/Status/Status';
import { StatusCategory } from '../../../../../../../_base-shared/models/Status/StatusCategory';
import { User } from '../../../../../../../_base-shared/models/User/User';
import { environment } from '../../../../../environments/environment';
import { MainGlobalEventService } from '../../../../_shared/services/main-global-event.service';
import { PackagerService } from '../../../../packager/packager.service';
import { AdministratorsService } from '../../../address-book/administrators/administrators.service';
import { CourtService } from '../../../address-book/court/court.service';
import { NotariesService } from '../../../address-book/notaries/notaries.service';
import { SolicitorsService } from '../../../address-book/solicitors/solicitors.service';
import { AffiliateService } from '../../../affiliate/affiliate.service';
import { CallStatusService } from '../../../call-status/call-status.service';
import { CreditorService } from '../../../creditor/creditor.service';
import { DepartmentService } from '../../../department/department.service';
import { PaymentStatusService } from '../../../payment-status/payment-status.service';
import { StatusPickerTrait } from '../../../status/status-picker.trait';
import { StatusService } from '../../../status/status.service';
import { CaseCreditorService } from '../../case-creditor.service';
import { ProductService } from '../../product.service';

@Component({
  standalone: false,
  selector:    'app-creditor-cases-list-filters',
  templateUrl: './creditor-cases-list-filters.component.html',
  styleUrls:   ['./creditor-cases-list-filters.component.scss']
})
export class CreditorCasesListFiltersComponent extends StatusPickerTrait implements OnInit, OnDestroy {
  @Input() onlyRelated: boolean = null;

  @Output() filtersReady  = new EventEmitter<boolean>();
  @Output() submitFilters = new EventEmitter<any>();

  @Output("parentFilter") parentFilter: EventEmitter<any>             = new EventEmitter();
  @Output("parentClearFilters") parentClearFilters: EventEmitter<any> = new EventEmitter();
  @Output("acceptPlans") acceptPlans: EventEmitter<any>               = new EventEmitter();

  public isLoading                                 = 0;
  public form: UntypedFormGroup;
  public statusFormControlName                     = 'case_status_ids';
  public dateRadioControl: UntypedFormControl      = new UntypedFormControl();
  public statusCategoryControl: UntypedFormControl = new UntypedFormControl([]);
  public statusControl: UntypedFormControl         = new UntypedFormControl([]);

  public caseCreditorListFilter: CaseCreditorListFilter;
  public users: Array<User>                              = [];
  public creditors: Array<Creditor>                      = [];
  public allStatuses: Array<Status>                      = [];
  public statusCategories: Array<StatusCategory>         = [];
  public filteredStatusCategories: Array<StatusCategory> = [];
  public caseCreditorStatuses: Array<any>                = [];
  public products: Array<Product>                        = [];
  public isUserCreditorNegotiator: boolean               = false;

  private formChangeSubscriber: Subscription;
  private localStorageName: string;
  public authUser: User;

  public creditorTypes: Array<AppSelectOption>;
  public canSettleOptions: Array<AppSelectOption>;
  public departmentCategories: Array<DepartmentCategory> = [];

  constructor(private fb: UntypedFormBuilder,
              private globalEventsService: MainGlobalEventService,
              private translate: TranslateService,
              private statusService: StatusService,
              private creditorService: CreditorService,
              private departmentService: DepartmentService,
              private caseCreditorService: CaseCreditorService,
              private productService: ProductService) {
    super('case_status_ids', true, true);
  }

  ngOnInit(): void {
    this.creditorTypes          = this.getCreditorType();
    this.canSettleOptions       = this.getCanSettleOptions();
    this.caseCreditorListFilter = this.getFiltersFromStorage();
    this.filtersReady.emit(false);
    this.subscriptions.push(this.globalEventsService.authUser$.subscribe(user => {
      this.authUser = user;
      if (this.authUser.department_assignments.find(
        departmentAssignment => departmentAssignment.department.name === 'Creditor Negotiator')
      ) {
        this.onlyRelated              = false;
        this.isUserCreditorNegotiator = true;
      }
      this.buildForm(this.caseCreditorListFilter, this.authUser);
    }));
    this.fetchCreditors();
    this.fetchStatuses();
    this.getCreditorStatusOptions();
    this.fetchDepartmentCategories();
    this.fetchProducts();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.formChangeSubscriber.unsubscribe();
  }

  private getFiltersFromStorage(): CaseCreditorListFilter {
    const filter                    = new CaseCreditorListFilter();
    let data                        = JSON.parse(localStorage.getItem(this.localStorageName));
    data                            = data ? data : {};
    filter.search                   = data.search ? data.search : null;
    filter.start_date               = data.start_date ? new Date(data.start_date) : null;
    filter.end_date                 = data.end_date ? new Date(data.end_date) : null;
    filter.status_date_type         = data.status_date_type ? data.status_date_type : 'sign_up';
    filter.activity_status          = data.activity_status ? data.activity_status : 'active';
    filter.case_creditor_status_ids = data.case_creditor_status_ids ? data.case_creditor_status_ids : [];
    filter.select_all               = data.select_all !== undefined ? data.select_all : 0;
    filter.amount_paid              = data.amount_paid ? data.amount_paid : null;
    filter.case_status_ids          = data.case_status_ids ? data.case_status_ids : [];
    filter.creditor_ids             = data.creditor_ids ? data.creditor_ids : [];
    filter.creditor_types           = data.creditor_types ? data.creditor_types : null;
    filter['creditor-negotiator']   = data['creditor-negotiator'] ? data['creditor-negotiator'] : [];
    filter.settlement_funds         = data.settlement_funds ? data.settlement_funds : null;
    filter.reduction_percentage     = data.reduction_percentage ? data.creditoreduction_percentager_types : null;
    filter.product_ids              = data.product_ids ? data.product_ids : null;
    filter.days_in_status           = data.days_in_status ? data.days_in_status : null;
    filter.can_settle               = data.can_settle ? data.can_settle : null;

    return filter;
  }

  private buildForm(caseCreditorListFilter: CaseCreditorListFilter, authUser: User): void {
    if (this.formChangeSubscriber) {
      this.formChangeSubscriber.unsubscribe();
    }

    this.form = this.fb.group({
      search:                   [caseCreditorListFilter.search],
      start_date:               [caseCreditorListFilter.start_date],
      end_date:                 [caseCreditorListFilter.end_date],
      status_date_type:         [caseCreditorListFilter.status_date_type],
      activity_status:          [caseCreditorListFilter.activity_status],
      case_creditor_status_ids: [caseCreditorListFilter.case_creditor_status_ids],
      creditor_ids:             [caseCreditorListFilter.creditor_ids],
      case_status_ids:          [caseCreditorListFilter.case_status_ids],
      select_all:               [caseCreditorListFilter.select_all],
      amount_paid:              [caseCreditorListFilter.amount_paid],
      creditor_types:           [caseCreditorListFilter.creditor_types],
      settlement_funds:         [caseCreditorListFilter.settlement_funds],
      reduction_percentage:     [caseCreditorListFilter.reduction_percentage],
      product_ids:              [caseCreditorListFilter.product_ids],
      days_in_status:           [caseCreditorListFilter.days_in_status],
      can_settle:               [caseCreditorListFilter.can_settle],
      'creditor-negotiator':    [caseCreditorListFilter['creditor-negotiator']],
      // only_related:                [caseCreditorListFilter.only_related],
    });

    this.filtersReady.emit(true);
    this.submitFilters.emit(this.caseCreditorListFilter);
    this.subscribeToFormChanges();
  }

  private subscribeToFormChanges() {
    this.formChangeSubscriber = this.form.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(res => {
      if (this.form.invalid) {
        return;
      }
      this.caseCreditorListFilter = this.form.value;
      // TODO: update filters in query string
      this.storeFiltersToStorage(this.caseCreditorListFilter);
      this.submitFilters.emit(this.caseCreditorListFilter);
    });
  }


  private fetchCreditors(): void {
    this.isLoading++;
    this.subscriptions.push(
      this.creditorService.index({select_all: 1, active: 1}).pipe(finalize(() => this.isLoading--))
        .subscribe(result => this.creditors = result.data),
    );
  }

  private fetchStatuses(): void {
    this.isLoading++;
    this.subscriptions.push(
      this.statusService.indexCategoriesWithStatuses().pipe(finalize(() => this.isLoading--))
        .subscribe(result => {
            this.statusCategories = result.data
            // if status_id is 2 add to docs status options

            this.statusCategories         = this.statusCategories.filter(category => category.id !== 405);
            this.filteredStatusCategories = result.data;
            this.statusCategories.forEach(category => {
              this.allStatuses.push(category);
              category.statuses.forEach(status => this.allStatuses.push(status));
            });
            this.setStatusControls(this.form.get(this.statusFormControlName).value);
          },
        ),
    );
  }


  public clearFilters(): void {
    localStorage.removeItem(this.localStorageName);
    this.caseCreditorListFilter = this.getFiltersFromStorage();
    this.filtersReady.emit(false);
    this.buildForm(this.caseCreditorListFilter, this.authUser);
    this.dateRadioControl.patchValue(null);
    this.statusCategoryControl.patchValue([]);
    this.statusControl.patchValue([]);
  }

  public patchFilter(name: string, value, options = {}): void {
    this.form.get(name).patchValue(value, options);
  }

  private storeFiltersToStorage(filter: CaseCreditorListFilter): void {
    return localStorage.setItem(this.localStorageName, JSON.stringify(filter));
  }

  public clearFormControl($event, name) {
    $event.preventDefault();
    $event.stopPropagation();
    this.form.get(name).patchValue(null);
  }

  public clearMultiSelect($event, name: string) {
    $event.stopPropagation();
    this.form.get(name).patchValue([]);
  }

  public dateModifierChange($event) {
    this.form.get('start_date').setValue(DateTime.local().startOf($event.value).toJSDate());
    this.form.get('end_date').setValue(DateTime.local().endOf($event.value).toJSDate());
  }

  public dateChanged($event: MatDatepickerInputEvent<any>, formControlName, endDay = false) {
    if (endDay) {
      const inputValue = $event.value.toString();
      const dt         = DateTime.fromJSDate(new Date(inputValue));
      const endOfDay   = dt.endOf('day');
      const date       = endOfDay.toJSDate();
      this.form.get(formControlName).patchValue(date);
    }
  }

  public selectStatuses(active: boolean) {
    const selectedStatusIds = [];
    this.allStatuses.forEach(status => {
      if (((active && status.flag_case_active) || (!active && !status.flag_case_active))) {
        selectedStatusIds.push(status.id);
      }
    });
    this.setStatusControls(selectedStatusIds);
    this.form.get(this.statusFormControlName).updateValueAndValidity();
  }

  public userIsAMami(): boolean {
    return environment.DISTRIBUTION_USER_IDS.includes(this.authUser.id);
  }

  private getCreditorStatusOptions(): void {
    this.caseCreditorService.getCaseCreditorStatuses({select_all: 1})
      .pipe(finalize(() => this.isLoading--))
      .subscribe(
        res => {
          if (this.authUser.locale === 'en') {
            this.caseCreditorStatuses = res.data.sort((a, b) => a.name_en.localeCompare(b.name_en));
          } else {
            this.caseCreditorStatuses = res.data.sort((a, b) => a.name_es.localeCompare(b.name_es));
          }
        });
  }

  private getCreditorType(): Array<{ label: string, value: string }> {
    return [
      {label: this.translate.instant('CREDITORS.bank'), value: 'bank'},
      {label: this.translate.instant('CREDITORS.collections'), value: 'collections'},
      {label: this.translate.instant('CREDITORS.high-interest'), value: 'high_interest'},
      {label: this.translate.instant('CREDITORS.standard'), value: 'standard'},
      {label: this.translate.instant('CREDITORS.consumer_finance'), value: 'consumer_finance'},
    ];
  }

  private fetchDepartmentCategories(): void {
    this.subscriptions.push(
      this.departmentService.categoryIndex(['departments.users']).pipe(finalize(() => this.isLoading--))
        .subscribe(result => {
          this.departmentCategories = result.data.filter(item => {
            item.departments = item.departments.filter(department => department.type === 'creditor-negotiator');

            return item.departments.length > 0;
          });
        }),
    );
  }

  private fetchProducts(): void {
    this.isLoading++;
    this.productService.index({select_all: 1}).pipe(
      finalize(() => this.isLoading--)
    ).subscribe({
      next:  result => {
        this.products = result.data.filter(product => product.active);
      },
      error: error => console.log(error)
    });
  }

  private getCanSettleOptions(): Array<{ label: string, value: string }> {
    return [
      {label: this.translate.instant('CASES.list.filters.can_settle.options.yes'), value: '1'},
      {label: this.translate.instant('CASES.list.filters.can_settle.options.no'), value: '0'},
    ];
  }

}
